/**
 * GoalRecapScreen
 *
 * First screen in the plan builder.
 * Shows the user's goal pre-filled from onboarding with an
 * option to change it. Confirms and moves forward.
 */

import React, { useRef, useState } from "react";
import { View, Text, Pressable, ScrollView, Animated } from "react-native";
import { Snowflake, Baby, Heart, FlaskConical } from "lucide-react-native";
import type { LucideIcon } from "lucide-react-native";
import { EMBER } from "@louise/moon-ui/theme/tokens";
import WarmButton from "@louise/moon-ui/primitives/WarmButton";
import { useTranslations, type TranslationFunction } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { usePlanBuilder } from "../PlanBuilderContext";
import type { HomepageGender, HomepageGoal } from "../../../homepage.types";

type GoalOptionDef = {
  id: HomepageGoal;
  labelKey: string;
  labelKeyByGender?: Partial<Record<HomepageGender, string>>;
  detailKey: string;
  detailKeyByGender?: Partial<Record<HomepageGender, string>>;
  icon: LucideIcon;
  /** If set, only show this goal for this gender */
  genderOnly?: HomepageGender;
};

type GoalOption = {
  id: HomepageGoal;
  label: string;
  detail: string;
  icon: LucideIcon;
};

const ALL_GOALS: GoalOptionDef[] = [
  {
    id: "preserve",
    labelKey: "plan_builder.goal_recap.preserve_label",
    labelKeyByGender: {
      female: "plan_builder.goal_recap.preserve_label_female",
      male: "plan_builder.goal_recap.preserve_label_male",
    },
    detailKey: "plan_builder.goal_recap.preserve_detail",
    detailKeyByGender: {
      female: "plan_builder.goal_recap.preserve_detail_female",
      male: "plan_builder.goal_recap.preserve_detail_male",
    },
    icon: Snowflake,
  },
  {
    id: "embryo-preserve",
    labelKey: "plan_builder.goal_recap.embryo_label",
    detailKey: "plan_builder.goal_recap.embryo_detail",
    icon: FlaskConical,
  },
  {
    id: "pregnancy",
    labelKey: "plan_builder.goal_recap.pregnancy_label",
    detailKey: "plan_builder.goal_recap.pregnancy_detail",
    icon: Baby,
  },
  {
    id: "egg-donation",
    labelKey: "plan_builder.goal_recap.egg_donation_label",
    detailKey: "plan_builder.goal_recap.egg_donation_detail",
    icon: Heart,
    genderOnly: "female",
  },
  {
    id: "sperm-donation",
    labelKey: "plan_builder.goal_recap.sperm_donation_label",
    detailKey: "plan_builder.goal_recap.sperm_donation_detail",
    icon: Heart,
    genderOnly: "male",
  },
];

/** Filter goals by gender and resolve gender-specific labels */
function getGoalsForGender(
  gender: HomepageGender | null,
  t: TranslationFunction,
): GoalOption[] {
  return ALL_GOALS.filter((g) => !g.genderOnly || g.genderOnly === gender).map(
    (g) => ({
      id: g.id,
      icon: g.icon,
      label: t((gender && g.labelKeyByGender?.[gender]) ?? g.labelKey),
      detail: t((gender && g.detailKeyByGender?.[gender]) ?? g.detailKey),
    }),
  );
}

function OptionButton({
  goal,
  isSelected,
  onPress,
}: {
  goal: GoalOption;
  isSelected: boolean;
  onPress: () => void;
}) {
  const scale = useRef(new Animated.Value(1)).current;
  const Icon = goal.icon;

  const handlePressIn = () => {
    Animated.spring(scale, {
      toValue: 0.97,
      useNativeDriver: true,
    }).start();
  };

  const handlePressOut = () => {
    Animated.spring(scale, {
      toValue: 1,
      friction: 4,
      useNativeDriver: true,
    }).start();
  };

  return (
    <Pressable
      onPress={onPress}
      onPressIn={handlePressIn}
      onPressOut={handlePressOut}
      accessibilityRole="button"
      accessibilityState={{ selected: isSelected }}
      // Inert testability hook: react-native-web 0.21 no longer forwards
      // accessibilityState.selected to the DOM, so mirror it via the
      // aria-selected prop for deterministic E2E assertions.
      aria-selected={isSelected}
    >
      <Animated.View
        style={{
          transform: [{ scale }],
          flexDirection: "row",
          alignItems: "center",
          paddingHorizontal: 18,
          paddingVertical: 16,
          gap: 14,
          backgroundColor: isSelected
            ? `${EMBER.colors.text}10`
            : "rgba(255, 255, 255, 0.82)",
          borderRadius: 14,
          borderWidth: 1,
          borderColor: isSelected
            ? EMBER.colors.text
            : "rgba(255, 255, 255, 0.9)",
          shadowColor: "#000",
          shadowOffset: { width: 0, height: 1 },
          shadowOpacity: 0.04,
          shadowRadius: 8,
        }}
      >
        <Icon
          size={20}
          color={isSelected ? EMBER.colors.text : EMBER.colors.textMid}
          strokeWidth={1.5}
        />
        <View style={{ flex: 1 }}>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 15,
              fontWeight: "500",
              color: isSelected ? EMBER.colors.text : EMBER.colors.text,
            }}
          >
            {goal.label}
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              color: EMBER.colors.textLight,
              marginTop: 1,
            }}
          >
            {goal.detail}
          </Text>
        </View>
        {/* Radio indicator */}
        <View
          style={{
            width: 22,
            height: 22,
            borderRadius: 11,
            borderWidth: 2,
            borderColor: isSelected ? EMBER.colors.text : EMBER.colors.border,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {isSelected && (
            <View
              style={{
                width: 12,
                height: 12,
                borderRadius: 6,
                backgroundColor: EMBER.colors.text,
              }}
            />
          )}
        </View>
      </Animated.View>
    </Pressable>
  );
}

type Props = {
  onNext: () => void;
};

export default function GoalRecapScreen({ onNext }: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("GoalRecapScreen");
  const { state, updateState } = usePlanBuilder();
  const [selected, setSelected] = useState<HomepageGoal | null>(state.goal);
  const goals = getGoalsForGender(state.gender, t);
  const handleSelect = (goal: HomepageGoal) => {
    setSelected(goal);
    updateState({ goal });
  };

  return (
    <ScrollView
      contentContainerStyle={{ padding: 24, paddingBottom: 40 }}
      showsVerticalScrollIndicator={false}
    >
      <View style={{ marginBottom: 24 }}>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 22,
            fontWeight: "700",
            color: EMBER.colors.text,
            lineHeight: 28,
          }}
        >
          {t("plan_builder.goal_recap.title")}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 15,
            fontWeight: "400",
            color: EMBER.colors.textLight,
            lineHeight: 22,
            marginTop: 6,
          }}
        >
          {t("plan_builder.goal_recap.subtitle")}
        </Text>
      </View>

      {/* Goal options */}
      <View style={{ gap: 10 }}>
        {goals.map((goal) => (
          <OptionButton
            key={goal.id}
            goal={goal}
            isSelected={selected === goal.id}
            onPress={() => {
              capturePress(pressLabel(goal.label), goal.id);
              handleSelect(goal.id);
            }}
          />
        ))}
      </View>

      {selected && (
        <View style={{ marginTop: 24 }}>
          <WarmButton
            title={t("plan_builder.continue")}
            onPress={() => onNext()}
          />
        </View>
      )}
    </ScrollView>
  );
}
